import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext.js';

function UsageCircleView({ usageLeft, usageTotal, resetAt, color = 'green', abonnementVar }) {
  const { profile } = useAuth();
  const [timeToReset, setTimeToReset] = useState('');
  const [effectiveUsage, setEffectiveUsage] = useState({ left: usageLeft || 0, total: usageTotal || 0, resetAt: resetAt || null });

  // Persist and restore last known usage per profile
  const storageKey = useMemo(() => {
    const keyPart = (profile?.user?.id || profile?.id || profile?.email || 'anon');
    return `usageCircle:last:${keyPart}`;
  }, [profile]);

  useEffect(() => {
    // If we have valid totals, use them and persist
    if ((usageTotal || 0) > 0) {
      const next = { left: usageLeft || 0, total: usageTotal || 0, resetAt: resetAt || null };
      setEffectiveUsage(next);
      try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch (_) {}
      return;
    }
    // No data provided → try to restore last known for this profile
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        setEffectiveUsage({ left: parsed.left || 0, total: parsed.total || 0, resetAt: parsed.resetAt || null });
      }
    } catch (_) {}
  }, [usageLeft, usageTotal, resetAt, storageKey]);

  // If gold, fractionLeft = 1.0; else usageLeft/usageTotal (guarding for zero).
  const displayLeft = effectiveUsage.left || 0;
  const displayTotal = effectiveUsage.total || 0;
  const displayResetAt = effectiveUsage.resetAt || resetAt;

  const fractionLeft =  displayTotal > 0 ? displayLeft / displayTotal : 0;

  // Decide ring color based on fractionLeft.
  // (Mirroring the Swift switch/case logic as closely as possible)
  const ringColor = (() => {
    if (fractionLeft >= 0.6) {
      return 'green';
    } else if (fractionLeft >= 0.3) {
      return 'orange';
    } else if (fractionLeft >= 0.1) {
      return 'orange';
    } else {
      return 'red';
    }
  })();

  // Called once usageLeft=0 to show countdown to reset time
  const updateTimeToReset = () => {
    if (!displayResetAt) {
      setTimeToReset('--:--:--');
      return;
    }

    const now = new Date();
    const resetDate = new Date(displayResetAt);
    
    const diff = Math.floor((resetDate - now) / 1000);
    if (diff <= 0) {
      setTimeToReset('00:00:00');
      return;
    }

    const hours = Math.floor(diff / 3600);
    const minutes = Math.floor((diff % 3600) / 60);
    const seconds = diff % 60;

    setTimeToReset(
      `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
    );
  };

  useEffect(() => {
    if (usageLeft <= 0) {
      // Update once immediately
      updateTimeToReset();
      // Then update every second
      const interval = setInterval(updateTimeToReset, 1000);
      return () => clearInterval(interval);
    }
  }, [usageLeft, resetAt]);

  // For the circular progress bar, we use an SVG with two circles:
  //  - A "track" circle in the background (gray)
  //  - A "progress" circle whose stroke-dashoffset is set by fractionLeft
  const size = 40;
  const strokeWidth = 4;
  const center = size / 2;
  const radius = center - strokeWidth / 2;
  const circumference = 2 * Math.PI * radius;
  const progressOffset = circumference - fractionLeft * circumference;



  // Determine font size based on number of digits
  let usageFontSize = 20;
  if (String(usageLeft).length === 2) usageFontSize = 16;
  if (String(usageLeft).length >= 3) usageFontSize = 13;



 if (displayLeft > 0) {
    // Standard usage => fraction
    return (
      <div
        style={styles.container}
        onClick={() => {}}
        role="button"
        tabIndex={0}
      >
        <svg width={size} height={size} style={styles.svg}>
          {/* Background circle */}
          <circle
            stroke="gray"
            fill="transparent"
            strokeOpacity="0.2"
            cx={center}
            cy={center}
            r={radius}
            strokeWidth={strokeWidth}
          />
          {/* Progress circle */}
          <circle
            stroke={color || ringColor}
            fill="transparent"
            strokeWidth={strokeWidth}
            cx={center}
            cy={center}
            r={radius}
            strokeDasharray={circumference}
            strokeDashoffset={progressOffset}
            style={{
              transform: 'rotate(-90deg)',
              transformOrigin: '50% 50%',
              transition: 'stroke-dashoffset 0.3s ease-in-out',
            }}
          />
          {/* usageLeft text in the center */}
          <text
            x="50%"
            y="50%"
            textAnchor="middle"
            dominantBaseline="central"
            style={{
              fill: color || ringColor,
              fontSize: usageFontSize,
              fontWeight: 'bold',
              fontFamily: 'system-ui',
            }}
          >
            {displayLeft}
          </text>
        </svg>
      </div>
    );
  } else {
    // usageLeft=0 => show lock + countdown or reserved message
    return (
      <div
        style={{ ...styles.container}}
        onClick={() => {}}
        role="button"
        tabIndex={0}
      >
        <div style={styles.lockContainer}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ color: 'red' }}>🔒</span>
            {abonnementVar === 'gold' ? (
              <span style={{ color: 'red', fontSize: '0.75rem', textAlign: 'center' }}>Réservé aux <br/> abonnements Gold</span>
            ) : (
              <span style={{ color: 'red', fontSize: '0.75rem' }}>{timeToReset}</span>
            )}
          </div>
          <div>
            <span style={{ color: 'red', fontSize: '0.75rem' }}>
              {abonnementVar === 'gold' ? '' : timeToReset}
            </span>
          </div>
        </div>
      </div>
    );
  }
}

const styles = {
  container: {
    display: 'inline-block',
    padding: '5px',
  },
  svg: {
    display: 'block',
  },
  lockContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
};

export default UsageCircleView;
